import addIcon from '@iconify/icons-material-symbols/add-rounded';
import deleteIcon from '@iconify/icons-material-symbols/delete-outline-rounded';
import { Icon } from '@iconify/react';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';

import useLocales from 'hooks/useLocales';

import { ChatSession } from './chatSessionStore';

// ----------------------------------------------------------------------

const CHECKBOX_CLASS = 'chat-session-checkbox';

interface ChatSessionListProps {
    sessions: ChatSession[];
    activeSessionId: string | null;
    // While a turn is in flight, starting or switching chats is disabled so a
    // reply can't be misfiled against the wrong session.
    busy: boolean;
    onSelect: (id: string) => void;
    onNew: () => void;
    // One or more ids — the trash button on a row, or the selected ones.
    onRequestDelete: (ids: string[]) => void;
}

const ChatSessionList: React.FunctionComponent<ChatSessionListProps> = ({
    sessions,
    activeSessionId,
    busy,
    onSelect,
    onNew,
    onRequestDelete,
}) => {
    const { translate } = useLocales();
    const ordered = [...sessions].sort((a, b) => b.updatedAt - a.updatedAt);
    const [checked, setChecked] = useState<string[]>([]);
    // Ids of chats deleted since they were ticked drop out by themselves.
    const selected = useMemo(
        () => checked.filter(id => sessions.some(session => session.id === id)),
        [checked, sessions],
    );
    const allSelected = sessions.length > 0 && selected.length === sessions.length;

    const toggle = (id: string): void =>
        setChecked(current => (current.includes(id) ? current.filter(item => item !== id) : [...current, id]));

    return (
        <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 2 }}>
            <Button
                fullWidth
                variant='outlined'
                startIcon={<Icon icon={addIcon} width={20} height={20} />}
                onClick={onNew}
                disabled={busy}
                sx={{ mb: 2 }}
            >
                {translate('chat_new_conversation')}
            </Button>

            {/* Shown once something is ticked, so the list stays plain until it's needed. */}
            {selected.length > 0 && (
                <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    spacing={1}
                    sx={{ mb: 1, pl: 0.5 }}
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                size='small'
                                checked={allSelected}
                                indeterminate={!allSelected}
                                disabled={busy}
                                onChange={() => setChecked(allSelected ? [] : sessions.map(session => session.id))}
                            />
                        }
                        label={
                            <Typography variant='body2'>
                                {translate('chat_select_all')} ({selected.length}/{sessions.length})
                            </Typography>
                        }
                    />
                    <Button
                        size='small'
                        color='error'
                        startIcon={<Icon icon={deleteIcon} width={18} height={18} />}
                        disabled={busy}
                        onClick={() => onRequestDelete(selected)}
                    >
                        {translate('chat_delete_selected')}
                    </Button>
                </Stack>
            )}

            {ordered.length === 0 ? (
                <Typography variant='body2' sx={{ color: 'text.secondary', textAlign: 'center', mt: 2 }}>
                    {translate('chat_empty_hint')}
                </Typography>
            ) : (
                <List disablePadding>
                    {ordered.map(session => (
                        <ListItemButton
                            key={session.id}
                            selected={session.id === activeSessionId}
                            disabled={busy}
                            onClick={() => onSelect(session.id)}
                            sx={{
                                borderRadius: 1,
                                mb: 0.5,
                                pr: 1,
                                pl: 0.5,
                                // Hidden (but keeping its space, so nothing shifts) until the row is
                                // hovered or focused; all shown once anything is ticked, and always
                                // on touch screens, which have no hover to reveal them.
                                [`& .${CHECKBOX_CLASS}`]: {
                                    opacity: selected.length > 0 ? 1 : 0,
                                    transition: 'opacity 150ms',
                                },
                                [`&:hover .${CHECKBOX_CLASS}, &:focus-within .${CHECKBOX_CLASS}`]: { opacity: 1 },
                                [`@media (hover: none)`]: { [`& .${CHECKBOX_CLASS}`]: { opacity: 1 } },
                            }}
                        >
                            <Checkbox
                                className={CHECKBOX_CLASS}
                                size='small'
                                checked={selected.includes(session.id)}
                                disabled={busy}
                                inputProps={{ 'aria-label': session.title || translate('chat_untitled') }}
                                // Ticking a chat shouldn't also open it.
                                onClick={event => event.stopPropagation()}
                                onChange={() => toggle(session.id)}
                                sx={{ p: 0.5, mr: 0.5 }}
                            />
                            <ListItemText
                                primary={session.title || translate('chat_untitled')}
                                primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                            />
                            <IconButton
                                edge='end'
                                size='small'
                                aria-label={translate('chat_delete_confirm')}
                                onClick={event => {
                                    // Don't let the trash click also select the row.
                                    event.stopPropagation();
                                    onRequestDelete([session.id]);
                                }}
                            >
                                <Icon icon={deleteIcon} width={20} height={20} />
                            </IconButton>
                        </ListItemButton>
                    ))}
                </List>
            )}
        </Box>
    );
};

export default ChatSessionList;
